import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown, Check, AlertCircle, Lock, ShieldAlert } from 'lucide-react';
import { organizationQueryOptions, userProfileQueryOptions } from '@/lib/query-options';
import { UserRole, MembershipRole } from '@/lib/types';
import { STRIPE_CONFIG, apiEndpoint } from '@/lib/consts';
import fetchWithAuth from '@/lib/apis';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import type { Stripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';

type PlanType = 'SMB' | 'TEST';

// Lazy-load Stripe only when needed to avoid loading telemetry on every page
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(STRIPE_CONFIG.publishableKey)
    );
  }
  return stripePromise;
};

interface PaymentUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Called after successful payment completion
  returnUrl?: string; // Optional custom return URL after Stripe checkout
  source?: string; // Track where the modal was triggered from (e.g., 'deploy', 'report')
}

export const PaymentUpgradeModal = ({
  isOpen,
  onClose,
  onSuccess,
  returnUrl,
  source = 'unknown'
}: PaymentUpgradeModalProps) => {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('SMB');
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutInstance, setCheckoutInstance] = useState<StripeEmbeddedCheckout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const organizationData = useAtomValue(organizationDataAtom);
  const orgId = organizationData?.id;

  const { data: organization } = useQuery({
    ...organizationQueryOptions(orgId!),
    enabled: !!orgId && isOpen
  });

  const { data: userProfile } = useQuery({
    ...userProfileQueryOptions(),
    enabled: isOpen
  });

  // Check if user is admin (can see TEST plan option)
  const isAdmin = userProfile?.role === UserRole.ADMIN;

  // Check if user is the organization owner (required for billing)
  const isOwner = organization?.membership?.role === MembershipRole.OWNER;

  // Find the organization owner from the users list
  const organizationOwner = useMemo(() => {
    if (!organization?.users) return null;
    return organization.users.find(user => user.membership?.role === MembershipRole.OWNER);
  }, [organization?.users]);

  // Track when modal is viewed
  useEffect(() => {
    if (isOpen && organization) {
      posthog?.capture('payment_modal_viewed', {
        organization_id: organization.id,
        current_plan: organization.plan_type,
        source,
      });
    }
  }, [isOpen, organization, posthog, source]);

  // Handle successful payment (called when returnUrl is hit with session_id)
  const handlePaymentSuccess = useCallback(() => {
    if (!isOpen) return;

    // Invalidate organization data to get updated plan
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
    queryClient.invalidateQueries({ queryKey: ['organizations', orgId] });

    posthog?.capture('payment_completed', {
      organization_id: organization?.id,
      plan_type: selectedPlan,
      source,
    });

    // Call success callback after a short delay to ensure webhook processed
    setTimeout(() => {
      onSuccess?.();
      handleClose();
    }, 1000);
  }, [isOpen, queryClient, orgId, posthog, organization?.id, selectedPlan, source, onSuccess]);

  // Check for Stripe success redirect
  useEffect(() => {
    if (!isOpen) return;

    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      handlePaymentSuccess();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isOpen, handlePaymentSuccess]);

  const handleClose = () => {
    // Clean up checkout instance
    if (checkoutInstance) {
      checkoutInstance.destroy();
      setCheckoutInstance(null);
    }

    // Clear the DOM element to ensure no residual Stripe elements
    const checkoutElement = document.getElementById('payment-modal-checkout');
    if (checkoutElement) {
      checkoutElement.innerHTML = '';
    }

    // Track modal close
    posthog?.capture('payment_modal_closed', {
      organization_id: organization?.id,
      completed_payment: false,
      source,
    });

    setShowCheckout(false);
    setError(null);
    setSelectedPlan('SMB'); // Reset to default
    onClose();
  };

  const handleUpgrade = async () => {
    if (!orgId || checkoutLoading || showCheckout) return;

    // Track upgrade button click
    posthog?.capture('payment_upgrade_clicked', {
      organization_id: organization?.id,
      selected_plan: selectedPlan,
      source,
    });

    setCheckoutLoading(true);
    setError(null);

    try {
      // Clean up any existing checkout instance
      if (checkoutInstance) {
        try {
          checkoutInstance.destroy();
        } catch (e) {
          console.warn('Error destroying checkout:', e);
        }
        setCheckoutInstance(null);

        // Wait for destroy to complete
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Clear the DOM element to ensure no residual Stripe elements
      const checkoutElement = document.getElementById('payment-modal-checkout');
      if (checkoutElement) {
        checkoutElement.innerHTML = '';
      }

      // Determine return URL
      const checkoutReturnUrl = returnUrl || `${window.location.origin}${window.location.pathname}`;

      // Create checkout session
      const response = await fetchWithAuth(
        `${apiEndpoint}/organizations/${orgId}/billing/create-checkout-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            return_url: checkoutReturnUrl,
            plan_type: selectedPlan, // Pass plan type to backend
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { client_secret } = await response.json();

      if (!client_secret) {
        throw new Error('No client secret returned from server');
      }

      // Load Stripe (lazy-loaded on first use)
      const stripe = await getStripe();
      if (!stripe) throw new Error('Failed to load Stripe');

      // Create embedded checkout
      const checkout = await stripe.initEmbeddedCheckout({
        clientSecret: client_secret,
      });

      setCheckoutInstance(checkout);
      setShowCheckout(true);

      // Mount after state update
      setTimeout(() => {
        checkout.mount('#payment-modal-checkout');
      }, 100);

      // Track successful checkout mount
      posthog?.capture('payment_checkout_mounted', {
        organization_id: organization?.id,
        selected_plan: selectedPlan,
        source,
      });
    } catch (error) {
      console.error('Failed to create checkout session:', error);

      const errorMessage = error instanceof Error ? error.message : 'Failed to load payment checkout';
      setError(errorMessage);

      // Track checkout error
      posthog?.capture('payment_checkout_error', {
        organization_id: organization?.id,
        error: errorMessage,
        source,
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!organization) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`${showCheckout ? 'max-w-6xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-gray-900">
            {showCheckout ? 'Complete Your Payment' : 'Upgrade Required'}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div
            className="border-l-4 rounded-lg p-4 mb-4"
            style={{
              backgroundColor: '#FEE2E2',
              borderLeftColor: '#B91C1C',
              borderTop: '1px solid #FCA5A5',
              borderRight: '1px solid #FCA5A5',
              borderBottom: '1px solid #FCA5A5'
            }}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#B91C1C' }} />
              <div className="text-gray-900">{error}</div>
            </div>
          </div>
        )}

        {/* Non-owner view - show permission error */}
        {!isOwner && !showCheckout ? (
          <div className="space-y-6">
            <div
              className="border-l-4 rounded-lg p-4"
              style={{
                backgroundColor: '#FEF3C7',
                borderLeftColor: '#A16207',
                borderTop: '1px solid #FDE68A',
                borderRight: '1px solid #FDE68A',
                borderBottom: '1px solid #FDE68A'
              }}
            >
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#A16207' }} />
                <div>
                  <p className="text-gray-900 font-medium">Owner Permission Required</p>
                  <p className="text-gray-700 text-sm mt-1">
                    Only the organization owner can upgrade the billing plan.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Organization Owner</p>
              {organizationOwner ? (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                    <span className="text-[#290BD9] font-medium">
                      {organizationOwner.name?.charAt(0)?.toUpperCase() || organizationOwner.email?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    {organizationOwner.name && (
                      <p className="text-gray-900 font-medium">{organizationOwner.name}</p>
                    )}
                    <p className="text-gray-700 text-sm">{organizationOwner.email}</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Unable to load owner information</p>
              )}
            </div>

            <p className="text-sm text-gray-700">
              Please contact the organization owner to upgrade your plan, or ask them to transfer ownership to you.
            </p>

            <Button
              onClick={handleClose}
              variant="outline"
              size="lg"
              className="w-full py-6"
            >
              Close
            </Button>
          </div>
        ) : showCheckout ? (
          <div>
            <div id="payment-modal-checkout" className="min-h-[500px]" />
          </div>
        ) : (
          <div className="space-y-6">
            <DialogDescription className="text-base text-gray-900 leading-relaxed">
              Complete Google Ads management and competitor monitoring powered by AI. Create campaigns in minutes, then let our automation optimize bids and keywords daily to maximize your ROI 24/7.
            </DialogDescription>

            {/* Plan Selection - SMB shown to all, TEST only for admins */}
            <div className="space-y-3">
              {isAdmin && <p className="text-sm font-medium text-gray-900">Select Plan:</p>}
              <div className={`grid gap-3 ${isAdmin ? 'grid-cols-2' : 'grid-cols-1 max-w-xs mx-auto'}`}>
                <div
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedPlan === 'SMB'
                      ? 'border-[#290BD9] bg-[#290BD9]/10'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } ${isAdmin ? 'cursor-pointer' : ''}`}
                  onClick={isAdmin ? () => setSelectedPlan('SMB') : undefined}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Crown className="h-5 w-5 text-[#290BD9]" />
                    {selectedPlan === 'SMB' && (
                      <Check className="h-5 w-5 text-[#290BD9]" />
                    )}
                  </div>
                  <p className="text-lg font-semibold text-gray-900">SMB Plan</p>
                  <p className="text-sm text-gray-700 mt-1">€499/month</p>
                </div>

                {isAdmin && (
                  <button
                    onClick={() => setSelectedPlan('TEST')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedPlan === 'TEST'
                        ? 'border-[#290BD9] bg-[#290BD9]/10'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Crown className="h-5 w-5 text-amber-600" />
                      {selectedPlan === 'TEST' && (
                        <Check className="h-5 w-5 text-[#290BD9]" />
                      )}
                    </div>
                    <p className="text-lg font-semibold text-gray-900">Test Plan</p>
                    <p className="text-sm text-gray-700 mt-1">€1/month</p>
                    <p className="text-xs text-amber-700 mt-1 font-medium">Admin Only</p>
                  </button>
                )}
              </div>
            </div>

            {/* Benefits List */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">What's included:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  "AI campaign generation",
                  "Daily keyword & bid optimization",
                  "Smart negative keyword detection",
                  "Daily performance insights",
                  "Competitor tracking & insights",
                  "Monitor and train the AI over time"
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center space-x-2">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-900">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Upgrade Button */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleUpgrade}
                disabled={checkoutLoading}
                size="lg"
                className="w-full py-6 bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center space-x-2"
              >
                {checkoutLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Loading checkout...
                  </>
                ) : (
                  'Upgrade Now'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                <span>Flexible monthly billing • Cancel anytime •</span>
                <Lock className="h-3 w-3" />
                <span>Secure payment by Stripe</span>
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
